// Stream a POST'd SSE response from the backend.
//
// We can't use EventSource here because the chat endpoint takes a JSON
// body (and EventSource only supports GET). fetch() + ReadableStream
// is the standard browser-side workaround. The backend emits one JSON
// object per `data:` line; this helper parses each line and calls
// `onEvent(evt)`.

import { authorizationHeader } from '../../utils/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export async function streamChatMessage({
  threadId,
  content,
  // Optional grounding for the model only (never stored or shown in the
  // thread) — used by scoped surfaces like the Movers Ask panel.
  context,
  onEvent,
  signal,
}) {
  // Resolves the hub access token (renewing it first if it's about to
  // expire), falling back to a local-mode Bearer or the SSO cookie.
  const headers = { 'Content-Type': 'application/json', ...(await authorizationHeader()) };

  const response = await fetch(
    `${API_BASE_URL}/api/chat/threads/${threadId}/messages`,
    {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(context ? { content, context } : { content }),
      signal,
    },
  );

  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const err = await response.json();
      detail = err.detail || err.message || detail;
    } catch (e) {
      /* ignore */
    }
    throw new Error(detail);
  }
  if (!response.body) {
    throw new Error('Streaming not supported in this environment.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  // The wire format is OpenAI-style SSE: one or more `data: …` lines
  // per event, terminated by a blank line. We accumulate until we see
  // a `\n\n` then split on it; partial lines stay in the buffer until
  // the next chunk arrives.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let separatorIdx;
    while ((separatorIdx = buffer.indexOf('\n\n')) !== -1) {
      const rawEvent = buffer.slice(0, separatorIdx);
      buffer = buffer.slice(separatorIdx + 2);
      const dataLines = rawEvent
        .split('\n')
        .filter(line => line.startsWith('data:'))
        .map(line => line.slice(5).trimStart());
      if (!dataLines.length) continue;
      const payload = dataLines.join('\n');
      try {
        onEvent(JSON.parse(payload));
      } catch (e) {
        // Skip malformed lines instead of killing the whole stream —
        // a transient bad chunk shouldn't drop the user's reply.
        // eslint-disable-next-line no-console
        console.warn('Skipping bad SSE payload:', payload);
      }
    }
  }
}
